/**
 * RtlBaseRatesSection — editable grid for the global RTL base-rate sheet
 * (rtl_base_rates collection, one row per product). One column per term-in-months.
 *
 * Rates are stored as annual DECIMAL FRACTIONS (0.105 = 10.50%); this editor edits
 * in PERCENT for usability and converts on save. Correspondents add a per-product
 * spread on top (Pricing tab) — this sheet is the shared base.
 *
 * Heavy Rehab is a SECONDARY classification of Fix and Flip (prices off its own
 * row); it's rendered indented under its parent. Small fixed product set with
 * edit-all-then-save → hand-rolled editable table (same rationale as
 * TierMarginsSection), not AG Grid.
 */
import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import PercentOutlined from "@mui/icons-material/PercentOutlined";

import Flx from "../../components/layout/Flx";
import Txt from "../../components/typography/Txt";
import { useSnackbar } from "notistack";
import { useCorrespondentsApi } from "../../hooks/useCorrespondentsHook";
import PricingConfigVersionDialog from "./PricingConfigVersionDialog";
import { teal } from "../../theme/colors";

const sectionCardSx = {
  border: "1px solid #e5e7eb",
  borderRadius: 3.5,
  bgcolor: "#fff",
  overflow: "hidden",
};
const sectionTitleSx = { fontSize: 14, fontWeight: 600, color: "#0f172a" };

const DEFAULT_TERMS = ["12", "18", "24"];

// percent string <-> decimal fraction (0.105 <-> "10.5")
const decToPct = (dec) =>
  dec == null || dec === "" ? "" : String(+(Number(dec) * 100).toFixed(4));
const pctToDec = (pct) =>
  pct === "" || pct == null || Number.isNaN(Number(pct))
    ? null
    : Number((Number(pct) / 100).toFixed(6));

export default function RtlBaseRatesSection() {
  const api = useCorrespondentsApi();
  const { enqueueSnackbar } = useSnackbar();
  const [products, setProducts] = useState([]); // taxonomy [{key,label,secondary_of}]
  const [drafts, setDrafts] = useState({}); // product -> { label, rates: {term: pctStr} }
  const [saved, setSaved] = useState({});
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [historyKey, setHistoryKey] = useState(null);
  const [newTerm, setNewTerm] = useState("");

  const load = () => {
    setLoading(true);
    return api
      .listRtlBaseRates()
      .then((data) => {
        const taxonomy = Array.isArray(data?.products) ? data.products : [];
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        setProducts(taxonomy);
        const byKey = {};
        rows.forEach((r) => {
          const key = r._id ?? r.product;
          if (key) byKey[key] = r;
        });
        // Union of all terms present, falling back to the defaults.
        const termSet = new Set(DEFAULT_TERMS);
        rows.forEach((r) =>
          Object.keys(r.ratesByTerm || {}).forEach((t) => termSet.add(String(t))),
        );
        const sortedTerms = [...termSet].sort((a, b) => Number(a) - Number(b));
        setTerms(sortedTerms);

        const d = {};
        taxonomy.forEach((p) => {
          const row = byKey[p.key] || {};
          const rates = {};
          sortedTerms.forEach((t) => {
            rates[t] = decToPct(row.ratesByTerm?.[t]);
          });
          d[p.key] = { label: row.label ?? "", rates };
        });
        setDrafts(d);
        setSaved(JSON.parse(JSON.stringify(d)));
      })
      .catch(() => {
        setProducts([]);
        setDrafts({});
        setSaved({});
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orderedProducts = useMemo(() => {
    // Parents first, each immediately followed by its secondaries.
    const parents = products.filter((p) => !p.secondary_of);
    const out = [];
    parents.forEach((p) => {
      out.push(p);
      products
        .filter((c) => c.secondary_of === p.key)
        .forEach((c) => out.push(c));
    });
    // Any orphan secondaries whose parent isn't listed.
    products
      .filter((p) => p.secondary_of && !parents.some((x) => x.key === p.secondary_of))
      .forEach((p) => out.push(p));
    return out;
  }, [products]);

  const setRate = (key, term, value) =>
    setDrafts((prev) => ({
      ...prev,
      [key]: { ...prev[key], rates: { ...prev[key].rates, [term]: value } },
    }));

  const isDirty = (key) =>
    JSON.stringify(drafts?.[key]) !== JSON.stringify(saved?.[key]);

  const addTerm = () => {
    const t = String(parseInt(newTerm, 10));
    if (!newTerm || Number.isNaN(Number(t)) || terms.includes(t)) {
      setNewTerm("");
      return;
    }
    setTerms((prev) => [...prev, t].sort((a, b) => Number(a) - Number(b)));
    setDrafts((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        next[k] = { ...v, rates: { ...v.rates, [t]: "" } };
      });
      return next;
    });
    setNewTerm("");
  };

  const saveProduct = (key) => {
    const d = drafts[key];
    const ratesByTerm = {};
    Object.entries(d.rates).forEach(([term, pct]) => {
      const dec = pctToDec(pct);
      if (dec != null) ratesByTerm[term] = dec;
    });
    const body = { label: d.label || undefined, ratesByTerm };
    setSavingKey(key);
    api
      .upsertRtlBaseRate(key, body)
      .then(() => {
        enqueueSnackbar("Base rates saved.", { variant: "success" });
        return load();
      })
      .catch((e) =>
        enqueueSnackbar(
          `Save failed: ${e?.response?.data?.detail || e?.message || e}`,
          { variant: "error" },
        ),
      )
      .finally(() => setSavingKey(null));
  };

  return (
    <Box sx={sectionCardSx}>
      <Flx ac jb g={1} sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #eef2f5" }}>
        <Flx ac g={1.5}>
          <PercentOutlined sx={{ fontSize: 18, color: teal[700] }} />
          <Txt sx={sectionTitleSx}>RTL Base Rates</Txt>
          <Txt sx={{ fontSize: 11.5, color: "#94a3b8" }}>
            Annual % per product × term. Correspondents add a spread on top.
          </Txt>
        </Flx>
        <Flx ac g={0.75}>
          <TextField
            size="small"
            placeholder="Term (mo)"
            value={newTerm}
            onChange={(e) => setNewTerm(e.target.value.replace(/[^0-9]/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && addTerm()}
            sx={{ width: 96, "& input": { py: 0.5, fontSize: 13 } }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddRounded sx={{ fontSize: 16 }} />}
            onClick={addTerm}
            sx={{ textTransform: "none" }}
          >
            Term
          </Button>
        </Flx>
      </Flx>

      <Box sx={{ p: 2.5 }}>
        {loading ? (
          <Flx center sx={{ py: 4 }}>
            <CircularProgress size={20} sx={{ color: teal[700] }} />
          </Flx>
        ) : orderedProducts.length === 0 ? (
          <Txt sx={{ fontSize: 13, color: "#94a3b8", py: 2 }}>
            No RTL products available.
          </Txt>
        ) : (
          <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, overflow: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
              <Box
                component="thead"
                sx={{
                  "& th": {
                    bgcolor: "#f1f6f9",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#475569",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    py: 1,
                    px: 1.5,
                    borderBottom: "1px solid #e5e7eb",
                    textAlign: "right",
                    whiteSpace: "nowrap",
                  },
                  "& th:first-of-type": { textAlign: "left" },
                  "& th:last-of-type": { textAlign: "center" },
                }}
              >
                <Box component="tr">
                  <Box component="th">Product</Box>
                  {terms.map((t) => (
                    <Box component="th" key={t}>
                      {t} mo
                    </Box>
                  ))}
                  <Box component="th">Actions</Box>
                </Box>
              </Box>
              <Box
                component="tbody"
                sx={{
                  "& td": {
                    py: 0.5,
                    px: 1.5,
                    borderBottom: "1px solid #f1f5f9",
                    verticalAlign: "middle",
                  },
                  "& tr:last-of-type td": { borderBottom: "none" },
                  "& tr:hover": { bgcolor: "#f8fafc" },
                }}
              >
                {orderedProducts.map((p) => {
                  const d = drafts[p.key];
                  if (!d) return null;
                  const dirty = isDirty(p.key);
                  const isSecondary = !!p.secondary_of;
                  return (
                    <Box component="tr" key={p.key}>
                      <Box
                        component="td"
                        sx={{
                          fontSize: 13,
                          fontWeight: isSecondary ? 500 : 700,
                          color: isSecondary ? "#475569" : "#0f172a",
                          pl: isSecondary ? 3 : 1.5,
                        }}
                      >
                        {isSecondary ? "↳ " : ""}
                        {p.label || p.key}
                      </Box>
                      {terms.map((t) => (
                        <Box component="td" key={t} sx={{ textAlign: "right" }}>
                          <TextField
                            type="number"
                            size="small"
                            value={d.rates[t] ?? ""}
                            onChange={(e) => setRate(p.key, t, e.target.value)}
                            inputProps={{ step: 0.05, style: { textAlign: "right" } }}
                            sx={{
                              width: 92,
                              "& input": {
                                py: 0.5,
                                fontSize: 13,
                                fontVariantNumeric: "tabular-nums",
                              },
                            }}
                          />
                        </Box>
                      ))}
                      <Box component="td" sx={{ textAlign: "center", whiteSpace: "nowrap" }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setHistoryKey(p.key)}
                          sx={{ minWidth: 0, px: 1, mr: 0.5, textTransform: "none" }}
                        >
                          <HistoryRounded sx={{ fontSize: 16 }} />
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          disabled={!dirty || savingKey === p.key}
                          onClick={() => saveProduct(p.key)}
                          sx={{ textTransform: "none", fontWeight: 600 }}
                        >
                          {savingKey === p.key ? "Saving…" : "Save"}
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <PricingConfigVersionDialog
        open={!!historyKey}
        onClose={() => setHistoryKey(null)}
        title={historyKey ? `${historyKey} — rate-sheet history` : ""}
        load={historyKey ? () => api.rtlBaseRateHistory(historyKey) : null}
      />
    </Box>
  );
}
